import type { SagaWorkflowGraphDto } from '@/dtos/SagaWorkflow'
import { Badge, Box, Flex, Link, Text } from '@chakra-ui/react'
import { Background, Controls, ReactFlow, ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useMemo } from 'react'
import { AuditEdge } from './auditEdge'
import { buildFlow } from './buildFlow'
import { StateNode } from './nodeTypes'
import { SelfLoopEdge } from './selfLoopEdge'
import { SubSagaGroup } from './SubSagaGroup'

// Render at most this many nested sub-saga levels, to bound a deep orchestrator tree.
const MAX_DEPTH = 3

// Defined outside the component so the references are stable across renders (React Flow requirement).
const sagaNodeTypes = { state: StateNode }
const sagaEdgeTypes = { auditEdge: AuditEdge, selfLoopEdge: SelfLoopEdge }

// The diagram is a precomputed, read-only flow: nothing is draggable/selectable and internal change events are ignored.
const noop = () => {}
const STATIC_FLOW_PROPS = {
  nodesDraggable: false,
  nodesConnectable: false,
  elementsSelectable: false,
  onNodesChange: noop,
  onEdgesChange: noop,
} as const

/**
 * Generic, presentational workflow diagram: renders any saga graph (definition skeleton + run overlay) with React Flow,
 * plus its FIFO serialization neighbours and nested sub-sagas. Re-renders on each live snapshot replacement.
 */
export const SagaWorkflowDiagram = ({ graph, depth = 0 }: { graph: SagaWorkflowGraphDto; depth?: number }) => {
  const { nodes, edges } = useMemo(() => buildFlow(graph), [graph])

  return (
    <Box>
      {graph.currentStateUnknown && (
        <Text fontSize="xs" color="orange.600" mb={2}>
          Aktuální stav „{graph.currentState}“ neodpovídá žádnému kroku definice – diagram nemusí být přesný.
        </Text>
      )}

      {(graph.fifoPredecessorId || graph.fifoSuccessorId) && (
        <Flex gap={2} mb={2} align="center" wrap="wrap">
          {graph.fifoPredecessorId && (
            <Badge colorScheme="purple" textTransform="none">
              <Link href={`Errors/Detail/${graph.fifoPredecessorId}`} target="_blank">◀ Čeká za předchozí sagou</Link>
            </Badge>
          )}
          {graph.fifoSuccessorId && (
            <Badge colorScheme="purple" textTransform="none">
              <Link href={`Errors/Detail/${graph.fifoSuccessorId}`} target="_blank">Následující saga ve frontě ▶</Link>
            </Badge>
          )}
        </Flex>
      )}

      <Box h="280px" borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="gray.50">
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={sagaNodeTypes}
            edgeTypes={sagaEdgeTypes}
            fitView
            fitViewOptions={{ padding: 0.15, minZoom: 0.2, maxZoom: 1 }}
            minZoom={0.2}
            proOptions={{ hideAttribution: true }}
            {...STATIC_FLOW_PROPS}
          >
            <Background gap={16} color="#EDF2F7" />
            <Controls showInteractive={false} />
          </ReactFlow>
        </ReactFlowProvider>
      </Box>

      {depth < MAX_DEPTH && graph.children.length > 0 && (
        <Box mt={3} display="grid" gap={2}>
          {graph.children.map(child => (
            <SubSagaGroup key={child.graph.id} child={child} depth={depth + 1} />
          ))}
        </Box>
      )}
    </Box>
  )
}
